/* eslint-disable @typescript-eslint/no-unused-vars */
import { useMediaQuery } from "@mui/material";
import { AnimatePresence } from "framer-motion";
import { ReactComponent as ClockIcon } from "assets/clock.svg";
import { endlessOfferAssets } from "assets/cdn";
import bgBrushMobile from "assets/limited-deals/bg-brush-mobile.png";
import bgBrush from "assets/limited-deals/bg-brush.png";
import CountdownV2 from "components/CountdownV2";
import { useWebStoreContext } from "contexts";
import DebugCopyIdButton from "debug/components/DebugCopyIdButton";
// import { useAppSelector } from "hooks";
import { useImagePath } from "hooks";
import { PurchaseOfferSingleItem } from "interfaces";
import { EndlessOfferResponse } from "interfaces/endless-offer";
import { offersApi, useGetFeatureTrackerQuery } from "redux/apis";
// import { selectPoisBountyV3State } from "redux/slices";
import {
	LandscapeAnimatedNodeSlot,
	PortraitAnimatedNodeSlot,
	PortraitSixAnimatedNodeSlot
} from "./animations";
import { getClassByNodesCount, getThemeConfig } from "./helpers";
import { EndlessCrab } from "./lib/EndlessCrab";
import EndlessNode from "./lib/EndlessNode";
// import { PoisBountyBadge } from "./lib/PoisBountyBadge";
import {
	BgBrush,
	Container,
	NodesContainer,
	NodesContainerSixPortrait,
	NodesContainerThreePortrait,
	PalmTrees,
	Rope,
	RopeSixPortrait,
	RopeSixPortraitSlot,
	RopeThreePortrait,
	RopesSixPortraitLayer,
	timerStyle,
	timerStyleMobile,
	Title
} from "./styles";

import { SectionSource } from "interfaces";

const getSixPortraitGridPosition = (index: number) => ({
	gridColumn: [0, 3, 4].includes(index) ? 1 : 2,
	gridRow: index < 2 ? 1 : index < 4 ? 2 : 3
});

const EnlessOffer = ({ from }: { from?: SectionSource }) => {
	const isPortrait = useMediaQuery("(max-width:733px)");
	const { nowTime } = useWebStoreContext();
	const { authToken } = useWebStoreContext();
	const { data: purchaseOffers, isLoading: isOffersLoading } =
		offersApi.endpoints.getOffers.useQueryState({
			offerType: "singleItem",
			isAuthenticated: !!authToken?.value
		});

	const {
		data: endlessOfferData,
		isLoading,
		isFetching,
		refetch
	}: {
		data?: EndlessOfferResponse;
		isLoading: boolean;
		isFetching: boolean;
		refetch: () => void;
	} = useGetFeatureTrackerQuery({
		FeatureType: "endless-offer",
		isAuthenticated: !!authToken?.value
	});

	// Temporarily hidden per product request; restore this state with the JSX below
	// to re-enable the Poi's Bounty tag.
	// const { canContribute: isPoisActive } = useAppSelector(
	// 	selectPoisBountyV3State
	// );
	const { getImage } = useImagePath("cdn");
	const bgPalmTreesMobile = getImage(
		endlessOfferAssets.main.backgroundPalmTreesMobile
	);
	const bgPalmTrees = getImage(endlessOfferAssets.main.backgroundPalmTrees);
	const ropeSix = getImage(endlessOfferAssets.main.ropeSix);
	const rope = getImage(endlessOfferAssets.main.rope);
	const theme = getThemeConfig(endlessOfferData?.tracker, isPortrait);

	const handleEnd = () => {
		setTimeout(() => {
			refetch();
		}, 1000);
	};

	if (isLoading || isOffersLoading) {
		return <></>;
	}
	if (!endlessOfferData?.tracker) {
		return <></>;
	}

	const { tracker } = endlessOfferData;

	const getNextSuperOfferCount = () => {
		const filteredNodes = tracker?.nodes.filter((node) => !node.completionTime);
		return tracker.nextSuperOfferIndex - filteredNodes[0].nodeIndex;
	};

	return (
		<Container $imageUrl={theme?.bgImageUrl}>
			{/*
				{isPoisActive && <PoisBountyBadge style={{ zIndex: 5 }} />}
			*/}
			<PalmTrees
				src={
					theme?.secondaryBgImageUrl
						? theme?.secondaryBgImageUrl
						: isPortrait
						? bgPalmTreesMobile
						: bgPalmTrees
				}
			/>
			<BgBrush src={isPortrait ? bgBrushMobile : bgBrush} />
			<CountdownV2
				icon={<ClockIcon />}
				endTime={tracker.endTime}
				style={isPortrait ? timerStyleMobile : timerStyle}
				startTime={nowTime}
				text="Offer ends in: "
				labelStyle={{ color: "#EB5757", marginLeft: "0.6em" }}
				onEnd={() => {
					handleEnd();
				}}
			/>
			<Title src={theme?.titleImageUrl} />
			{isPortrait ? (
				tracker.displayNodeCount > 5 ? (
					<NodesContainerSixPortrait>
						<RopesSixPortraitLayer>
							{Array.from({ length: 6 }, (_, index) => (
								<RopeSixPortraitSlot
									style={getSixPortraitGridPosition(index)}
									key={index}
								>
									{index === 0 && (
										<RopeSixPortrait
											className="first"
											src={theme?.ropeImageUrlSixNodes || ropeSix}
										/>
									)}
									{[2, 5].includes(index) && (
										<RopeSixPortrait
											src={theme?.ropeImageUrlSixNodes || ropeSix}
										/>
									)}
									{[3, 1].includes(index) && (
										<RopeSixPortrait
											className="vertical"
											src={theme?.ropeImageUrlSixNodes || ropeSix}
										/>
									)}
								</RopeSixPortraitSlot>
							))}
						</RopesSixPortraitLayer>
						<AnimatePresence>
							{tracker.nodes
								.filter((node) => !node.completionTime)
								.slice(0, tracker.displayNodeCount)
								.map((node, index) => (
									<PortraitSixAnimatedNodeSlot
										style={getSixPortraitGridPosition(index)}
										layout="position"
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										transition={{
											duration: 0.5,
											layout: { duration: 0.5 }
										}}
										key={node.nodeIndex}
									>
										<EndlessNode
											className={getClassByNodesCount(tracker.displayNodeCount)}
											node={node}
											offer={purchaseOffers?.offers.find(
												(offer: PurchaseOfferSingleItem) =>
													offer.id === node.offerId
											)}
											isFirstNode={index === 0}
											isFetching={isFetching}
											displayNodeCount={tracker.displayNodeCount}
											ribbonUrl={
												node.isSuperOffer
													? theme?.nodeSuperOfferBadgeRibbonImageUrl
													: theme?.nodeBadgeRibbonImageUrl
											}
											backgroundUrl={
												node.isSuperOffer
													? theme?.nodeSuperOfferBgImageUrl
													: theme?.nodeBgImageUrl
											}
											backgroundLockedUrl={
												node.isSuperOffer
													? theme?.nodeSuperOfferLockedBgImageUrl
													: theme?.nodeBgLockedImageUrl
											}
											from={from}
										/>
									</PortraitSixAnimatedNodeSlot>
								))}
						</AnimatePresence>
					</NodesContainerSixPortrait>
				) : (
					<NodesContainerThreePortrait>
						<RopeThreePortrait src={theme?.ropeImageUrlThreeNodes || rope} />
						<div
							style={{
								display: "grid",
								width: "100%",
								height: "100%",
								position: "relative",
								gridTemplateRows: "repeat(4, minmax(0, 1fr))",
								rowGap: "0.8em"
							}}
						>
							<AnimatePresence>
								{tracker.nodes
									.filter((node) => !node.completionTime)
									.slice(
										0,
										tracker.displayNodeCount === 3
											? 4
											: tracker.displayNodeCount
									)
									.map((node, index) => (
										<PortraitAnimatedNodeSlot
											layout="position"
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											transition={{
												duration: 0.5,
												layout: { duration: 0.5 }
											}}
											key={node.nodeIndex}
										>
											<EndlessNode
												className={getClassByNodesCount(
													tracker.displayNodeCount
												)}
												node={node}
												offer={purchaseOffers?.offers.find(
													(offer: PurchaseOfferSingleItem) =>
														offer.id === node.offerId
												)}
												isFirstNode={index === 0}
												isFetching={isFetching}
												displayNodeCount={tracker.displayNodeCount}
												ribbonUrl={
													node.isSuperOffer
														? theme?.nodeSuperOfferBadgeRibbonImageUrl
														: theme?.nodeBadgeRibbonImageUrl
												}
												backgroundUrl={
													node.isSuperOffer
														? theme?.nodeSuperOfferBgImageUrl
														: theme?.nodeBgImageUrl
												}
												backgroundLockedUrl={
													node.isSuperOffer
														? theme?.nodeSuperOfferLockedBgImageUrl
														: theme?.nodeBgLockedImageUrl
												}
												from={from}
											/>
										</PortraitAnimatedNodeSlot>
									))}
							</AnimatePresence>
						</div>
					</NodesContainerThreePortrait>
				)
			) : (
				<NodesContainer
					className={getClassByNodesCount(tracker.displayNodeCount)}
				>
					<Rope
						src={theme?.ropeImageUrlThreeNodes || rope}
						className={getClassByNodesCount(tracker.displayNodeCount)}
					/>
					<div
						style={{
							display: "grid",
							width: "100%",
							height: "100%",
							position: "relative",
							gridTemplateColumns: `repeat(${
								tracker.displayNodeCount === 3 ? 4 : tracker.displayNodeCount
							}, minmax(0, 1fr))`
						}}
					>
						<AnimatePresence>
							{tracker.nodes
								.filter((node) => !node.completionTime)
								.slice(
									0,
									tracker.displayNodeCount === 3 ? 4 : tracker.displayNodeCount
								)
								.map((node, index) => (
									<LandscapeAnimatedNodeSlot
										columns={
											tracker.displayNodeCount === 3
												? 4
												: tracker.displayNodeCount
										}
										layout="position"
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										transition={{
											duration: 0.5,
											layout: { duration: 0.5 }
										}}
										key={node.nodeIndex}
									>
										<EndlessNode
											className={getClassByNodesCount(tracker.displayNodeCount)}
											key={index}
											node={node}
											offer={purchaseOffers?.offers.find(
												(offer: PurchaseOfferSingleItem) =>
													offer.id === node.offerId
											)}
											isFirstNode={index === 0}
											isFetching={isFetching}
											displayNodeCount={tracker.displayNodeCount}
											ribbonUrl={
												node.isSuperOffer
													? theme?.nodeSuperOfferBadgeRibbonImageUrl
													: theme?.nodeBadgeRibbonImageUrl
											}
											backgroundUrl={
												node.isSuperOffer
													? theme?.nodeSuperOfferBgImageUrl
													: theme?.nodeBgImageUrl
											}
											backgroundLockedUrl={
												node.isSuperOffer
													? theme?.nodeSuperOfferLockedBgImageUrl
													: theme?.nodeBgLockedImageUrl
											}
											from={from}
										/>
									</LandscapeAnimatedNodeSlot>
								))}
						</AnimatePresence>
					</div>
				</NodesContainer>
			)}
			{tracker.showCountdown && (
				<EndlessCrab nextSuperOfferCount={getNextSuperOfferCount()} />
			)}
			<DebugCopyIdButton
				id={tracker.eventId}
				label="EventID"
				side="left"
				style={{ fontSize: "1.3em", zIndex: 4 }}
			/>
		</Container>
	);
};

export default EnlessOffer;
